import React, { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import Main from '../../layout/Main';
import Header from '../../layout/Header';
import Logo from '../../layout/Logo/Logo';
import Button from '../../buttons/Button';

import trimUserName from '../../../utils/trimUserName';
import { getFormattedDestination } from '../../../utils';
import { APP_PAGE_ORDER_LIST } from '../../../constants';
import { setAppPage } from '../../../redux/actions/appPage.actions';
import { setPickTourWithError } from '../../../redux/actions/orders.actions';
import { acknowledgePickTour } from '../../../redux/actions/acknowledgePickTour.actions';
import { getEmployee, selectPickerStoreID, getOrdersFromState } from '../../../redux/selectors';

import styles from './PickTourWithError.css';

const PickTourWithError = (): ReactElement | null => {
  const { t } = useTranslation();
  const translationRoot = 'ERROR_ORDER_SCREEN';
  const dispatch = useDispatch();
  const employee = useSelector(getEmployee);
  const pickerStore = useSelector(selectPickerStoreID);
  const { errorPickTour } = useSelector(getOrdersFromState);

  const returnToOrderList = () => {
    dispatch(setAppPage({ name: APP_PAGE_ORDER_LIST }));
    dispatch(setPickTourWithError({ errorPickTour: null }));
  };

  const handleAcknowledgeError = () => {
    if (errorPickTour) {
      dispatch(acknowledgePickTour({ pickTourID: errorPickTour.pickTourID }));
    }
  };

  const HeaderContents = () => {
    return (
      <>
        <div className={styles.leftContainer}>
          <Logo />
          <h1 className={styles.title}>{t('ORDER_LIST.PICK')}</h1>
        </div>
        <span className={styles.divider} />
        <div>
          <p>{`${t('UTILS.STORE')}#: ${String(Number(pickerStore)).padStart(5, '0')}`}</p>
          <p>{trimUserName(employee.name || '', window.innerWidth)}</p>
        </div>
      </>
    );
  };

  return errorPickTour ? (
    <>
      <Header>
        <div className={styles.headerContainer}>
          <HeaderContents />
        </div>
      </Header>
      <Main className={styles.main}>
        <ErrorOutlineIcon className={styles.errorCircle} />
        <h1 className={styles.h1}>{t(`${translationRoot}.ACKNOWLEDGE_ERROR_AND_DISMISS`)}</h1>
        <ul className={styles.ordersSummaryData}>
          {errorPickTour.metadata.ordersSummaryData
            .filter(orderInfo => orderInfo.errorCode && orderInfo.errorCode !== '0')
            .map(orderInfo => (
              <li key={orderInfo.orderNumber}>
                {t(`${translationRoot}.CUSTOMER`)} {getFormattedDestination(orderInfo.destination)} -{' '}
                {t(`${translationRoot}.ORDER`)} #{orderInfo.orderNumber} -{' '}
                {orderInfo.errorMessage || t(`${translationRoot}.FINALIZATION_ERROR_FALLBACK_MESSAGE`)}
              </li>
            ))}
        </ul>
        <div className={styles.footerContainer}>
          <Button color="secondary" onClick={returnToOrderList} data-cy="error-goback-button">
            {t(`${translationRoot}.GO_BACK`)}
          </Button>
          <Button onClick={handleAcknowledgeError} data-cy="error-acknowledge-button">
            {t(`${translationRoot}.ACKNOWLEDGE_ERROR_AND_DISMISS`)}
          </Button>
        </div>
      </Main>
    </>
  ) : null;
};

export default PickTourWithError;
